/**
 * LayoutPresetSelector — small button group letting the user switch
 * the analytics dashboard grid between curated presets (compact /
 * standard / power) without having to enter the drag-drop edit mode.
 *
 * Generic by design : doesn't know about portfolio-specific widgets.
 * Caller passes `presets` (id + label + tooltip) and gets back the
 * selected preset id via `onSelect`. Persistence + applying the
 * preset to the actual layout is the caller's responsibility.
 *
 * Created in Sprint V2.B4 (cf. `docs/v2/PORTFOLIO_OFFCHAIN_REFONTE.md`).
 */

import type { ReactNode } from 'react';

export interface LayoutPreset {
  /** Stable identifier persisted in localStorage. */
  id: string;
  /** Visible label (i18n-translated by the caller). */
  label: string;
  /** Tooltip / aria-description (i18n-translated by the caller). */
  description: string;
  /** Optional icon rendered before the label. */
  icon?: ReactNode;
}

interface LayoutPresetSelectorProps {
  presets: LayoutPreset[];
  /** Currently active preset id ; the matching button gets `aria-pressed`. */
  activePresetId: string;
  onSelect: (presetId: string) => void;
  /** Group accessible name. Defaults to "Disposition". */
  groupLabel?: string;
}

export function LayoutPresetSelector({
  presets,
  activePresetId,
  onSelect,
  groupLabel = 'Disposition',
}: LayoutPresetSelectorProps) {
  return (
    <div
      role="group"
      aria-label={groupLabel}
      style={{
        display: 'inline-flex',
        gap: '4px',
        padding: '4px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
      }}
    >
      {presets.map((preset) => {
        const isActive = preset.id === activePresetId;
        return (
          <button
            key={preset.id}
            type="button"
            onClick={() => onSelect(preset.id)}
            aria-pressed={isActive}
            title={preset.description}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              border: 'none',
              borderRadius: '6px',
              background: isActive ? 'var(--primary)' : 'transparent',
              color: isActive ? 'white' : 'var(--text)',
              fontSize: '12px',
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
          >
            {preset.icon}
            <span>{preset.label}</span>
          </button>
        );
      })}
    </div>
  );
}
